import { Asset, AssetState, AssetType } from '@/api/generatedApi';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useCompareCart } from '@/services/compareCart';
import { MatchData } from '@/types/matchData';
import { PATHS } from '@/utils/paths';
import { useState } from 'react';
import { useNavigate, useMatch, useSearchParams } from 'react-router-dom';

const isComparableAssetType = (assetType: AssetType | undefined): boolean =>
  assetType === AssetType.Model || assetType === AssetType.Scan;

const getDisabledReason = ({ asset }: { asset?: Asset }): string | undefined => {
  if (!asset) {
    return 'Asset not found';
  } else if (!isComparableAssetType(asset.type)) {
    return 'Asset must be a 3D model or scan';
  } else if (asset.state !== AssetState.Finished) {
    return 'Asset is not finished processing';
  }
  return undefined;
};

const getTooltip = ({
  isSelected,
  disabledReason,
  isComparePage,
}: {
  isSelected: boolean;
  disabledReason?: string;
  isComparePage: boolean;
}): string => {
  if (isSelected) {
    return isComparePage ? 'Remove from compare' : '';
  } else if (disabledReason) {
    return disabledReason;
  } else {
    return 'Select for compare';
  }
};

const removeMatchParameters = (parameters: URLSearchParams) => {
  parameters.delete('searchType');
  parameters.delete('matchPercentage');
  parameters.delete('forwardMatchPercentage');
  parameters.delete('reverseMatchPercentage');
};

export const useCompareButton = ({
  asset,
  quickCompare,
  matchData,
}: {
  asset?: Asset;
  quickCompare?: string;
  matchData?: MatchData;
}) => {
  const navigate = useNavigate();
  const isComparePage = !!useMatch(PATHS.COMPARE);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { selectedForCompare, setSelectedForCompare, isSelected, getCompareUrl } = useCompareCart();
  const { data } = useGetCurrentUser();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>();
  const { asset: quickCompareAsset } = useGetAsset({ assetId: quickCompare });

  const asset1Id = searchParameters.get('asset1Id');
  const asset2Id = searchParameters.get('asset2Id');
  const isAssetIdInSearchParameters = asset1Id === asset?.id || asset2Id === asset?.id;
  const isSelectedAsset = asset ? isAssetIdInSearchParameters || isSelected(asset.id) : false;
  const disabled = !isComparableAssetType(asset?.type) || asset?.state !== AssetState.Finished;
  const disabledReason = getDisabledReason({ asset });
  const tooltip = getTooltip({ isSelected: isSelectedAsset, disabledReason, isComparePage });

  const open = Boolean(anchorElement);
  const hasQuickCompare = quickCompare && isComparableAssetType(quickCompareAsset?.type);
  const hasSelectedAsset = !!selectedForCompare.asset;

  const shouldShowPopover = (hasQuickCompare || hasSelectedAsset) && !isSelectedAsset && !isComparePage;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!asset) {
      return;
    }

    if (isComparePage) {
      toggleSelection();
    } else if (isSelectedAsset) {
      setSelectedForCompare({});
    } else if (shouldShowPopover) {
      setAnchorElement(event.currentTarget);
    } else {
      handleSelectForCompare();
    }
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleSelectForCompare = () => {
    if (!asset) {
      return;
    }
    const assetWithMatchData = {
      ...asset,
      ...(matchData && quickCompare
        ? {
            matchData: {
              searchAssetId: quickCompare,
              ...matchData,
            },
          }
        : {}),
    };
    setSelectedForCompare({ asset: assetWithMatchData });
    handleClose();
  };

  const handleQuickCompare = () => {
    if (!asset || !quickCompareAsset) {
      return;
    }

    const compareUrl = getCompareUrl(quickCompareAsset, asset, data?.user?.settings, matchData);
    navigate(compareUrl);
    handleClose();
  };

  const handleCompareToSelected = () => {
    if (!asset || !selectedForCompare.asset) {
      return;
    }

    const compareUrl = getCompareUrl(selectedForCompare.asset, asset, data?.user?.settings, matchData);
    navigate(compareUrl);
    handleClose();
  };

  const toggleSelection = () => {
    if (!asset) {
      return;
    }

    if (asset1Id !== asset.id && asset2Id !== asset.id) {
      if (selectedForCompare.asset) {
        handleCompareToSelected();
      } else {
        handleSelectForCompare();
        const newParameters = new URLSearchParams(searchParameters);
        newParameters.set('asset1Id', asset.id);
        newParameters.set('tenant1Id', asset.tenantId);
        removeMatchParameters(newParameters);
        setSearchParameters(newParameters, { replace: true });
      }
      handleCompareToSelected();
      return;
    }

    if (asset1Id === asset.id) {
      const newParameters = new URLSearchParams(searchParameters);
      newParameters.delete('asset1Id');
      const tenant1Id = searchParameters.get('tenant1Id');
      if (tenant1Id) {
        newParameters.delete('tenant1Id');
      }
      removeMatchParameters(newParameters);
      setSearchParameters(newParameters, { replace: true });
      setSelectedForCompare({});
    }

    if (asset2Id === asset.id) {
      const newParameters = new URLSearchParams(searchParameters);
      newParameters.delete('asset2Id');
      const tenant2Id = searchParameters.get('tenant2Id');
      if (tenant2Id) {
        newParameters.delete('tenant2Id');
      }
      removeMatchParameters(newParameters);
      setSearchParameters(newParameters, { replace: true });
    }
  };

  return {
    anchorElement,
    disabled,
    handleClick,
    handleClose,
    handleQuickCompare,
    handleCompareToSelected,
    handleSelectForCompare,
    hasQuickCompare,
    hasSelectedAsset,
    isSelected: isSelectedAsset,
    open,
    quickCompareAsset,
    selectedAsset: selectedForCompare.asset,
    shouldShowPopover,
    tooltip,
  };
};
